import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  Globe,
  ShoppingCart,
  Search,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  FileText,
  Database,
  Bell,
  Calendar,
  ChevronDown,
  Store,
  BookOpen,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { seoAnalytics, seoAi } from '@/lib/api'
import BotSummaryCards from '@/components/seo/BotSummaryCards'
import BotActivityChart from '@/components/seo/BotActivityChart'
import CrawlErrorsTable from '@/components/seo/CrawlErrorsTable'
import TopCrawledUrlsTable from '@/components/seo/TopCrawledUrlsTable'
import TrafficOverviewCards from '@/components/seo/TrafficOverviewCards'
import TrafficChart from '@/components/seo/TrafficChart'
import TopLandingPagesTable from '@/components/seo/TopLandingPagesTable'
import AdsCampaignsTable from '@/components/seo/AdsCampaignsTable'
import ConversionsDashboard from '@/components/seo/ConversionsDashboard'
import ConversionsByLandingTable from '@/components/seo/ConversionsByLandingTable'
import GscConfigModal from '@/components/seo/GscConfigModal'
import GscSummaryCards from '@/components/seo/GscSummaryCards'
import GscQueriesTable from '@/components/seo/GscQueriesTable'
import SeoAlertsList from '@/components/seo/SeoAlertsList'
import TopCategoriesTable from '@/components/seo/TopCategoriesTable'
import TopProductsTable from '@/components/seo/TopProductsTable'

type TabType = 'bots' | 'traffic' | 'conversions' | 'ai' | 'search-console' | 'alerts'
type SectionType = 'shop' | 'blog' | undefined

const SECTION_OPTIONS = [
  { value: undefined, label: 'Todo', icon: Globe, description: 'Tienda + Blog' },
  { value: 'shop' as const, label: 'Tienda', icon: Store, description: 'Solo e-commerce' },
  { value: 'blog' as const, label: 'Blog', icon: BookOpen, description: 'Solo blog/wp' },
]

const tabs = [
  { id: 'bots' as TabType, name: 'Bots', icon: Bot, description: 'Actividad de rastreadores' },
  { id: 'traffic' as TabType, name: 'Trafico', icon: Globe, description: 'Organico y Google Ads' },
  { id: 'conversions' as TabType, name: 'Conversiones', icon: ShoppingCart, description: 'Atribucion de ventas' },
  { id: 'ai' as TabType, name: 'IA', icon: Sparkles, description: 'ChatGPT, Perplexity, Gemini' },
  { id: 'search-console' as TabType, name: 'Search Console', icon: Search, description: 'Keywords y posiciones' },
  { id: 'alerts' as TabType, name: 'Alertas', icon: Bell, description: 'Problemas detectados' },
]

// Date range presets
const DATE_PRESETS = [
  { days: 7, label: '7 dias' },
  { days: 14, label: '14 dias' },
  { days: 30, label: '30 dias' },
  { days: 60, label: '60 dias' },
  { days: 90, label: '90 dias' },
]

// Date range picker component
function DateRangePicker({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
}: {
  value: number | 'custom'
  onChange: (days: number | 'custom') => void
  customRange: { from: string; to: string }
  onCustomRangeChange: (range: { from: string; to: string }) => void
}) {
  const [showCustom, setShowCustom] = useState(value === 'custom')

  const today = new Date().toISOString().split('T')[0]
  const maxFrom = customRange.to || today
  const minTo = customRange.from || undefined

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm text-slate-500">Periodo:</span>

      {/* Preset buttons */}
      <div className="flex items-center gap-1.5">
        {DATE_PRESETS.map(({ days, label }) => (
          <button
            key={days}
            onClick={() => {
              onChange(days)
              setShowCustom(false)
            }}
            className={cn(
              'px-3 py-1.5 text-sm rounded-lg transition-colors',
              value === days && !showCustom
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-medium'
                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
            )}
          >
            {label}
          </button>
        ))}

        {/* Custom button */}
        <button
          onClick={() => {
            setShowCustom(!showCustom)
            if (!showCustom) onChange('custom')
          }}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors',
            showCustom
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 font-medium'
              : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
          )}
        >
          <Calendar className="w-4 h-4" />
          <span>Personalizado</span>
          <ChevronDown className={cn('w-3 h-3 transition-transform', showCustom && 'rotate-180')} />
        </button>
      </div>

      {/* Custom date inputs */}
      {showCustom && (
        <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-500">Desde:</label>
            <input
              type="date"
              value={customRange.from}
              max={maxFrom}
              onChange={(e) => onCustomRangeChange({ ...customRange, from: e.target.value })}
              className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-500">Hasta:</label>
            <input
              type="date"
              value={customRange.to}
              min={minTo}
              max={today}
              onChange={(e) => onCustomRangeChange({ ...customRange, to: e.target.value })}
              className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  )
}

// Section selector component (Tienda/Blog/Todo)
function SectionSelector({
  value,
  onChange,
}: {
  value: SectionType
  onChange: (section: SectionType) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-500">Seccion:</span>
      <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
        {SECTION_OPTIONS.map((option) => {
          const Icon = option.icon
          const isSelected = value === option.value
          return (
            <button
              key={option.label}
              onClick={() => onChange(option.value)}
              title={option.description}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-all',
                isSelected
                  ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-400 font-medium shadow-sm'
                  : 'text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Map phase names to readable Spanish
const PHASE_LABELS: Record<string, string> = {
  'connecting': 'Conectando al servidor...',
  'completed': 'Completado',
  'storing:bots': 'Guardando datos de bots...',
  'storing:traffic': 'Guardando datos de trafico...',
  'storing:ads': 'Guardando datos de anuncios...',
  'storing:sessions': 'Guardando sesiones...',
}

function getPhaseLabel(phase: string | null): string {
  if (!phase) return 'Preparando...'
  if (phase in PHASE_LABELS) return PHASE_LABELS[phase]
  if (phase.startsWith('reading:')) {
    const file = phase.replace('reading:', '')
    return `Leyendo ${file}...`
  }
  return phase
}

function SyncProgressPanel({ status, startTime }: { status: any; startTime: Date | null }) {
  const [elapsed, setElapsed] = useState(0)

  // Update elapsed time every second while syncing
  useEffect(() => {
    if (status?.status !== 'running' || !startTime) return
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [status?.status, startTime])

  if (!status || status.status !== 'running') return null

  const progress = status.progress_percent || 0
  const filesProcessed = status.files_processed || 0
  const filesTotal = status.files_total || 0
  const linesProcessed = status.lines_processed || 0
  const currentPhase = getPhaseLabel(status.current_phase)

  // Estimate remaining time based on progress
  const estimatedTotal = progress > 5 ? (elapsed / progress) * 100 : 0
  const estimatedRemaining = Math.max(0, Math.round(estimatedTotal - elapsed))

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-3 mb-3">
        <Loader2 className="w-5 h-5 text-amber-600 dark:text-amber-400 animate-spin" />
        <span className="font-medium text-amber-700 dark:text-amber-300">
          Sincronizacion en progreso
        </span>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-600 dark:text-slate-400">{currentPhase}</span>
          <span className="font-medium text-slate-700 dark:text-slate-300">{progress}%</span>
        </div>
        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-500 dark:bg-amber-400 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-400" />
          <div>
            <div className="text-slate-500 dark:text-slate-400">Archivos</div>
            <div className="font-medium text-slate-700 dark:text-slate-300">
              {filesProcessed} / {filesTotal}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-slate-400" />
          <div>
            <div className="text-slate-500 dark:text-slate-400">Lineas</div>
            <div className="font-medium text-slate-700 dark:text-slate-300">
              {linesProcessed.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-400" />
          <div>
            <div className="text-slate-500 dark:text-slate-400">Transcurrido</div>
            <div className="font-medium text-slate-700 dark:text-slate-300">
              {formatTime(elapsed)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-400" />
          <div>
            <div className="text-slate-500 dark:text-slate-400">Restante</div>
            <div className="font-medium text-slate-700 dark:text-slate-300">
              {progress > 5 ? `~${formatTime(estimatedRemaining)}` : '...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SyncStatusBadge({ status }: { status: any }) {
  if (!status) {
    return (
      <span className="badge badge-secondary">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Sin datos
      </span>
    )
  }

  if (status.status === 'running') {
    const progress = status.progress_percent || 0
    return (
      <span className="badge badge-warning">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        {progress}%
      </span>
    )
  }

  if (status.status === 'completed') {
    return (
      <span className="badge badge-success">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Sincronizado
      </span>
    )
  }

  if (status.status === 'failed' || status.status === 'error') {
    return (
      <span className="badge badge-danger">
        <AlertTriangle className="w-3 h-3 mr-1" />
        Error
      </span>
    )
  }

  return null
}

export default function SeoAnalyticsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('bots')
  const [section, setSection] = useState<SectionType>(undefined)
  const [dateMode, setDateMode] = useState<number | 'custom'>(30)
  const [customRange, setCustomRange] = useState(() => {
    const today = new Date()
    const thirtyDaysAgo = new Date(today)
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    return {
      from: thirtyDaysAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    }
  })
  const [syncStartTime, setSyncStartTime] = useState<Date | null>(null)
  const [showGscConfig, setShowGscConfig] = useState(false)
  const queryClient = useQueryClient()

  // Calculate effective days for API calls
  const daysFilter = useMemo(() => {
    if (dateMode === 'custom' && customRange.from && customRange.to) {
      const from = new Date(customRange.from)
      const to = new Date(customRange.to)
      const diffTime = Math.abs(to.getTime() - from.getTime())
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
    }
    return typeof dateMode === 'number' ? dateMode : 30
  }, [dateMode, customRange])

  // Status query (used for later phases)
  useQuery({
    queryKey: ['seo-status'],
    queryFn: () => seoAnalytics.getStatus(),
    refetchInterval: 10000, // Refetch every 10 seconds to check sync status
  })

  // Alert count query for badge
  const alertCountQuery = useQuery({
    queryKey: ['seo-alerts-count'],
    queryFn: () => seoAnalytics.getAlertCount(),
    refetchInterval: 60000, // Every minute
  })
  const alertCount = alertCountQuery.data?.data?.total || 0

  // Sync status query - faster polling when syncing
  const syncStatusQuery = useQuery({
    queryKey: ['seo-sync-status'],
    queryFn: () => seoAnalytics.getSyncStatus(),
    refetchInterval: (query) => {
      const data = query.state.data?.data
      return data?.status === 'running' ? 2000 : 10000 // Poll faster when syncing
    },
  })

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: (daysBack: number) => seoAnalytics.triggerLogSync(daysBack),
    onSuccess: () => {
      setSyncStartTime(new Date())
      queryClient.invalidateQueries({ queryKey: ['seo-sync-status'] })
    },
  })

  const syncStatus = syncStatusQuery.data?.data
  const isSyncing = syncStatus?.status === 'running'

  // Track when sync starts from database timestamp
  useEffect(() => {
    if (isSyncing && syncStatus?.started_at && !syncStartTime) {
      setSyncStartTime(new Date(syncStatus.started_at))
    } else if (!isSyncing && syncStartTime) {
      // Sync completed - reset and refresh data
      setSyncStartTime(null)
      queryClient.invalidateQueries({ queryKey: ['seo-bot-summary'] })
      queryClient.invalidateQueries({ queryKey: ['seo-bot-activity'] })
    }
  }, [isSyncing, syncStatus?.started_at, syncStartTime, queryClient])

  const handleSync = () => {
    if (!isSyncing) {
      syncMutation.mutate(1) // Sync last day
    }
  }

  const handleFullSync = () => {
    if (!isSyncing) {
      syncMutation.mutate(30) // Sync last 30 days
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">SEO Analytics</h1>
            <p className="page-subtitle">
              Analiza el rastreo de bots, trafico organico y conversiones
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Sync status */}
            <SyncStatusBadge status={syncStatus} />

            {/* Last sync time */}
            {syncStatus?.last_run && (
              <div className="flex items-center gap-1.5 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                <span>
                  {new Date(syncStatus.last_run).toLocaleString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            )}

            {/* Sync buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="btn btn-secondary btn-sm"
                title="Sincronizar ultimo dia"
              >
                <RefreshCw className={cn('w-4 h-4', isSyncing && 'animate-spin')} />
                <span className="hidden sm:inline">Sync</span>
              </button>
              <button
                onClick={handleFullSync}
                disabled={isSyncing}
                className="btn btn-primary btn-sm"
                title="Sincronizar ultimos 30 dias"
              >
                <RefreshCw className={cn('w-4 h-4', isSyncing && 'animate-spin')} />
                <span className="hidden sm:inline">Sync 30d</span>
              </button>
            </div>
          </div>
        </div>

        {/* Sync progress panel - shows when syncing */}
        <SyncProgressPanel status={syncStatus} startTime={syncStartTime} />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex gap-6 -mb-px" aria-label="Tabs">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'group relative flex items-center gap-2 py-4 px-1 text-sm font-medium transition-colors',
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                )}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.name}</span>
                {/* Alert badge */}
                {tab.id === 'alerts' && alertCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                    {alertCount > 99 ? '99+' : alertCount}
                  </span>
                )}
                {/* Active indicator */}
                <span
                  className={cn(
                    'absolute bottom-0 left-0 right-0 h-0.5 transition-colors',
                    isActive ? 'bg-primary-600 dark:bg-primary-400' : 'bg-transparent'
                  )}
                />
              </button>
            )
          })}
        </nav>
      </div>

      {/* Filters: Date range + Section */}
      <div className="flex flex-wrap items-center gap-6">
        <DateRangePicker
          value={dateMode}
          onChange={setDateMode}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
        <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 hidden sm:block" />
        <SectionSelector value={section} onChange={setSection} />
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'bots' && (
          <>
            <BotSummaryCards days={daysFilter} section={section} />
            <BotActivityChart days={daysFilter} section={section} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CrawlErrorsTable days={daysFilter} section={section} />
              <TopCrawledUrlsTable days={daysFilter} section={section} />
            </div>
          </>
        )}

        {activeTab === 'traffic' && (
          <>
            <TrafficOverviewCards days={daysFilter} section={section} />
            <TrafficChart days={daysFilter} section={section} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <TopCategoriesTable days={daysFilter} section={section} />
              <TopProductsTable days={daysFilter} section={section} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <TopLandingPagesTable days={daysFilter} section={section} />
              <AdsCampaignsTable days={daysFilter} section={section} />
            </div>
          </>
        )}

        {activeTab === 'conversions' && (
          <>
            <ConversionsDashboard days={daysFilter} section={section} />
            <div className="mt-6">
              <ConversionsByLandingTable days={daysFilter} section={section} />
            </div>
          </>
        )}

        {activeTab === 'ai' && (
          <AiTabContent days={daysFilter} />
        )}

        {activeTab === 'search-console' && (
          <GscTabContent
            days={daysFilter}
            onConfigClick={() => setShowGscConfig(true)}
          />
        )}

        {activeTab === 'alerts' && (
          <SeoAlertsList />
        )}
      </div>

      {/* GSC Config Modal */}
      <GscConfigModal
        isOpen={showGscConfig}
        onClose={() => setShowGscConfig(false)}
      />
    </div>
  )
}

// GSC Tab Content Component
function GscTabContent({ days, onConfigClick }: { days: number; onConfigClick: () => void }) {
  const statusQuery = useQuery({
    queryKey: ['gsc-status'],
    queryFn: () => seoAnalytics.getGscStatus(),
  })

  const status = statusQuery.data?.data

  // Not configured - show setup prompt
  if (!status?.configured) {
    return (
      <div className="card p-8 text-center">
        <Search className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
        <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
          Conectar Google Search Console
        </h3>
        <p className="text-slate-500 dark:text-slate-400 mb-4">
          Configura las credenciales OAuth para ver datos de keywords y posiciones.
        </p>
        <button onClick={onConfigClick} className="btn btn-primary">
          Configurar GSC
        </button>
      </div>
    )
  }

  // Configured - show data
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={onConfigClick} className="btn btn-secondary btn-sm">
          Configuracion GSC
        </button>
      </div>
      <GscSummaryCards days={days} />
      <GscQueriesTable days={days} />
    </>
  )
}

// AI channel tab: weekly summary + products cited by AI engines
function AiTabContent({ days }: { days: number }) {
  const weeks = Math.max(4, Math.min(52, Math.ceil(days / 7)))
  const summaryQuery = useQuery({
    queryKey: ['seo-ai-summary', weeks],
    queryFn: () => seoAi.getSummary(weeks),
  })
  const citationsQuery = useQuery({
    queryKey: ['seo-ai-citations', days],
    queryFn: () => seoAi.getCitations(days, 100),
  })

  const summary = summaryQuery.data?.data
  const citations = citationsQuery.data?.data
  const rows = summary?.rows || []
  const items = citations?.items || []
  const sources = Object.entries(summary?.totals_by_source || {}).sort((a, b) => b[1] - a[1])
  const maxVisits = rows.length > 0 ? Math.max(1, ...rows.map((r) => r.visits_total)) : 1
  const totalVisits = sources.reduce((acc, [, v]) => acc + v, 0)
  const label = (k: string) => summary?.labels?.[k] || k.replace('ai_', '')

  return (
    <>
      {/* Resumen por asistente */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Visitas desde IA</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{totalVisits.toLocaleString('es-ES')}</p>
          <p className="text-xs text-slate-500">ultimas {weeks} semanas (referer)</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Pedidos atribuidos</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{summary?.total_orders ?? 0}</p>
          <p className="text-xs text-slate-500">{(summary?.total_revenue ?? 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Fichas citadas</p>
          <p className="text-2xl font-semibold text-slate-900 dark:text-white">{citations?.total_products ?? 0}</p>
          <p className="text-xs text-slate-500">ultimos {days} dias</p>
        </div>
        <div className="card p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Citadas sin stock</p>
          <p className={cn('text-2xl font-semibold', (citations?.no_stock_count ?? 0) > 0 ? 'text-warning-600' : 'text-slate-900 dark:text-white')}>
            {citations?.no_stock_count ?? 0}
          </p>
          <p className="text-xs text-slate-500">trafico cualificado perdido</p>
        </div>
      </div>

      {/* Asistentes */}
      {sources.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Visitas por asistente</h3>
          <div className="flex flex-wrap gap-2">
            {sources.map(([k, v]) => (
              <span key={k} className="badge badge-secondary">
                {label(k)}: {v.toLocaleString('es-ES')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Semanal */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-slate-900 dark:text-white">Evolucion semanal del canal IA</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Visitas con referer de asistente (ps_connections_source), pedidos atribuidos y hits de bots IA de busqueda (OAI-SearchBot, ChatGPT-User, PerplexityBot...) vs entrenamiento
          </p>
        </div>
        <div className="card-body p-0 overflow-x-auto">
          {summaryQuery.isLoading ? (
            <div className="p-6 text-slate-400">Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-400">Sin datos todavia</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="text-left px-4 py-2">Semana</th>
                  <th className="text-left px-4 py-2 w-1/3">Visitas IA</th>
                  <th className="text-right px-4 py-2">ChatGPT</th>
                  <th className="text-right px-4 py-2">Otros</th>
                  <th className="text-right px-4 py-2">Pedidos</th>
                  <th className="text-right px-4 py-2">Ingresos</th>
                  <th className="text-right px-4 py-2">Bots busqueda</th>
                  <th className="text-right px-4 py-2">Bots entren.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {rows.map((r) => {
                  const chatgpt = r.visits_connections['ai_chatgpt'] || 0
                  return (
                    <tr key={r.yearweek}>
                      <td className="px-4 py-2 font-mono text-xs">{r.week_start}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-primary-500 rounded-full" style={{ width: `${Math.round((r.visits_total / maxVisits) * 100)}%` }} />
                          </div>
                          <span className="w-12 text-right tabular-nums">{r.visits_total}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{chatgpt}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.visits_total - chatgpt}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.orders}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.revenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.bot_hits_search.toLocaleString('es-ES')}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-400">{r.bot_hits_training.toLocaleString('es-ES')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Fichas citadas */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Fichas recomendadas por IA</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Hits de bots IA de busqueda, visitas referidas y pedidos por ficha · ultimos {days} dias
            </p>
          </div>
          {citations?.totals && (
            <span className="badge badge-secondary">
              {citations.totals.ai_visits} visitas · {citations.totals.ai_orders} pedidos
            </span>
          )}
        </div>
        <div className="card-body p-0 overflow-x-auto">
          {citationsQuery.isLoading ? (
            <div className="p-6 text-slate-400">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-slate-400">Sin fichas citadas en el periodo</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="text-left px-4 py-2">Ficha</th>
                  <th className="text-right px-4 py-2">Bots IA</th>
                  <th className="text-right px-4 py-2">Visitas IA</th>
                  <th className="text-right px-4 py-2">Pedidos</th>
                  <th className="text-right px-4 py-2">Ingresos</th>
                  <th className="text-right px-4 py-2">Stock</th>
                  <th className="text-right px-4 py-2">AI-ready</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {items.map((it) => (
                  <tr key={it.id_product} className={cn(it.stock <= 0 && 'bg-warning-50/50 dark:bg-warning-900/10')}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-900 dark:text-white truncate max-w-md" title={it.name}>{it.name || `#${it.id_product}`}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {it.reference}{it.mpn ? ` · ${it.mpn}` : ''} · #{it.id_product}
                        {it.sources.length > 0 && <span className="ml-2 text-slate-400">{it.sources.map((s) => s.replace('ai_', '')).join(', ')}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums" title={it.bots.join(', ')}>{it.bot_hits}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{it.ai_visits}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{it.ai_orders}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{it.ai_revenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                    <td className={cn('px-4 py-2 text-right tabular-nums', it.stock <= 0 && 'text-warning-600 font-semibold')}>{it.stock}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{it.ai_readiness_score ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}
